import { useContext, useState } from 'react';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { useQueryClient } from '@tanstack/react-query';
import { AdminMatches } from '@platform/client';
import { AdminTableContext } from '../../AdminTable';
import {
  Control,
  Controller,
  FormState,
  SubmitHandler,
  useForm,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import {
  FormsInputDropdown,
  FormsMultilineInput,
  InlineFormInput,
  FormsCheckBox,
  DatePicker,
} from '@platform/components';
import { MatchInitiationTypeEnum } from '@platform/client/schemas';

type NewIndividualMatchFormData = {
  professionalId: number;
  initiationType: MatchInitiationTypeEnum;
  personalMessage: string;
  representativeId?: number | null;
  projectId?: number | null;
  sendEmail: boolean;
  scheduledSendDate?: Date;
};

export const CreateIndividualMatchModal = (props: { onClose: () => void }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { control, formState, register, handleSubmit, watch } = useForm<NewIndividualMatchFormData>(
    {
      mode: 'onChange',
      reValidateMode: 'onChange',
    },
  );

  const queryClient = useQueryClient();
  const newIntroMutator = AdminMatches.useAdminCreateIndividualMatch({
    mutation: {
      onMutate: () => setModalStage('submitting'),
      onError: () => {
        setModalStage('error');
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            ...AdminMatches.getGetAdminMatchesQueryKey({
              ...adminQueryOptionsV2,
            }),
          ],
        });
        setModalStage('success');
      },
    },
  });

  const onSubmit: SubmitHandler<NewIndividualMatchFormData> = (data) => {
    if (!data.representativeId && !data.projectId) {
      // If neither representativeId nor projectId is provided block submit
      // error message is manually shown - workaround for multiple field validation on change
      return;
    }
    newIntroMutator.mutate({
      data: {
        professionalId: data.professionalId,
        initiationType: data.initiationType,
        introductionMessage: data.personalMessage,
        representativeId: data.representativeId || null,
        projectId: data.projectId || null,
        sendEmail: data.sendEmail ?? false,
        scheduledSendDate: data.scheduledSendDate?.toISOString(),
      },
    });
  };

  return (
    <AdminFormModal
      title='Create Individual Match'
      onClose={props.onClose}
      modalStage={modalStage}
      submitCTA={{
        text: 'Create',
        callback: () => {
          handleSubmit(onSubmit)();
        },
      }}
    >
      <NewIndividualMatchForm
        control={control}
        formState={formState}
        register={register}
        watch={watch}
      />
    </AdminFormModal>
  );
};

const NewIndividualMatchForm = (props: {
  control: Control<NewIndividualMatchFormData>;
  formState: FormState<NewIndividualMatchFormData>;
  register: UseFormRegister<NewIndividualMatchFormData>;
  watch: UseFormWatch<NewIndividualMatchFormData>;
}) => {
  const { control, formState, register, watch } = props;
  const repId = watch('representativeId');
  const projectId = watch('projectId');
  return (
    <div className='space-y-2'>
      <FormsInputDropdown
        type='default'
        control={control}
        name='initiationType'
        placeholder='Select initiation type'
        label='Initiation Type'
        rules={{ required: 'Initiation type is required' }}
        items={[
          { text: 'professional', textValue: 'professional' },
          { text: 'representative', textValue: 'representative' },
        ]}
        error={formState.errors.initiationType}
      />
      <InlineFormInput
        register={() => register('professionalId', { required: 'Pro ID is required' })}
        label='Professional ID'
        placeholder='Enter pro user id'
        errors={formState.errors.professionalId}
      />
      <div className='flex gap-4 items-end'>
        <InlineFormInput
          register={() => register('projectId')}
          label='Project ID'
          placeholder='Leave blank for custom'
          errors={undefined}
        />
        or
        <InlineFormInput
          register={() => register('representativeId')}
          label='Representative ID'
          placeholder='Enter rep user id'
          errors={undefined}
        />
      </div>
      {/* Could not find a clean solution without libraries to manage multiple field validation */}
      {formState.isSubmitted && !projectId && !repId && (
        <div className='text-left text-sm text-red-600'>Rep or project id required</div>
      )}
      {projectId && repId && (
        <div className='text-left text-sm text-gray-400'>
          Note: introduction will be created based on project rep
        </div>
      )}
      <FormsMultilineInput
        control={control}
        name='personalMessage'
        label='Personal Message'
        rules={{ required: 'Personal message is required' }}
        formsError={formState.errors.personalMessage}
      />
      <FormsCheckBox
        size='sm'
        description='Send new intro email and schedule reminders'
        control={control}
        name='sendEmail'
      />
      {watch('sendEmail') && (
        <Controller
          control={control}
          name='scheduledSendDate'
          render={({ field }) => (
            <DatePicker
              label='Schedule email (leave blank to send immediately)'
              initialDate={field.value ?? undefined}
              onChange={field.onChange}
            />
          )}
        />
      )}
    </div>
  );
};
